import { push } from "connected-react-router"
import {
    safePromise,
    execAndWaitForAll
} from "../../../Helpers/Functions/promise"
import {
    APICollaborationModel,
    APICollaborationParams,
    CollaboratorRole,
    CollaboratorStatus,
    defaultParams
} from "../../../Models/API/CollaboratorModel"
import { APIListContainer } from "../../../Models/API/Container"
import { getIdList, hasMore } from "../../../Models/Client/RemoteData"
import { MainThunkDispatch, State } from "../../../Reducers"
import { routePaths } from "../../../Routes"
import { apiEndpoints } from "../../../Routes/api"
import { failMessage, successMessage } from "../../Client/MessagesActions"
import { apiFetch } from "../../Client/NetworkActions"
import { APIAuthenticationType, CollaboratorType } from "../../Types"

const reloadProjectCollaborators =
    (appID: string) =>
    async (
        dispatch: MainThunkDispatch,
        getState: () => State
    ): Promise<void> => {
        const table = getState().api.collaborator.table[appID]
        const params =
            table === undefined || table.list === undefined
                ? defaultParams
                : table.list.params

        dispatch(getProjectCollaborators(appID, params))
    }

export const openProjectWorkQueue =
    (orgUsername: string, appID: string) =>
    async (dispatch: MainThunkDispatch) =>
        dispatch(push(routePaths.appDetail(orgUsername, appID)))

export const openProject =
    (orgUsername: string, appID: string) =>
    async (dispatch: MainThunkDispatch) =>
        dispatch(push(routePaths.appWelcome(orgUsername, appID)))

export const openProjectStatistics =
    (orgUsername: string, appID: string) =>
    async (dispatch: MainThunkDispatch) =>
        dispatch(push(routePaths.appDetailOverview(orgUsername, appID)))

const sendInvitation =
    (
        appID: string,
        email: string,
        role: CollaboratorRole,
        squashSuccessMessage: boolean = false,
        preventFetchList: boolean = false
    ) =>
    async (
        dispatch: MainThunkDispatch,
        getState: () => State
    ): Promise<void> => {
        const endpoint = apiEndpoints.sendInvite({})
        const { data, error } = await safePromise(
            dispatch(
                apiFetch<APICollaborationModel>(
                    `send_invite_${email}`,
                    endpoint,
                    {
                        method: "POST",
                        body: JSON.stringify({
                            appUuid: appID,
                            collaboratorRole: role,
                            email
                        })
                    }
                )
            )
        )

        if (error !== undefined || data === undefined) {
            dispatch(
                failMessage(`Could not send invitation to ${email}`, error)
            )
            throw error ?? new Error("Request throttled")
        }

        if (!squashSuccessMessage)
            dispatch(
                successMessage(
                    `You've successfully sent an invitation to ${email}`
                )
            )

        if (!preventFetchList) {
            dispatch(reloadProjectCollaborators(appID))
        }
    }

export const sendInvitations =
    (appID: string, emails: string[], role: CollaboratorRole) =>
    async (
        dispatch: MainThunkDispatch,
        getState: () => State
    ): Promise<void> => {
        try {
            await execAndWaitForAll(
                emails.map(email =>
                    dispatch(sendInvitation(appID, email, role, true, true))
                )
            )

            dispatch(successMessage(`You've successfully sent invitations`))
        } finally {
            dispatch(reloadProjectCollaborators(appID))
        }
    }

export const getProjectCollaborators =
    (
        instanceID: string,
        params: APICollaborationParams,
        page: number = 0,
        pageSize: number = 20
    ) =>
    async (dispatch: MainThunkDispatch, getState: () => State) => {
        const { data, error } = await safePromise(
            dispatch(
                apiFetch<APIListContainer<APICollaborationModel>>(
                    `GET_COLLAB_LIST_${instanceID}_${JSON.stringify({
                        page,
                        pageSize,
                        ...params
                    })}`,
                    apiEndpoints.getCollaborators({
                        appID: instanceID,
                        page,
                        pageSize,
                        sortBy: params.sort,
                        desc: params.desc
                    })
                )
            )
        )

        if (error !== undefined || data === undefined) {
            return
        }

        dispatch({
            type: CollaboratorType.setProjectCollaborators,
            meta: {
                instanceID,
                page,
                total: data.totalElements,
                params
            },
            payload: data.content
        })
    }

export const loadMoreCollaborators =
    (appID: string, pageSize?: number) =>
    async (dispatch: MainThunkDispatch, getState: () => State) => {
        const table = getState().api.collaborator.table[appID]

        if (table && hasMore(table) && table.list) {
            const pageSizeIncreased =
                pageSize !== undefined &&
                table.list.pages.length * pageSize > getIdList(table).length
            // If page size is increased, we need to scroll from initial page again
            const page = pageSizeIncreased ? 0 : table.list.pages.length

            await dispatch(
                getProjectCollaborators(
                    appID,
                    table.list.params,
                    page,
                    pageSize
                )
            )
        }
    }

const updateCollaborator =
    <T>(collaboratorID: number, appID: string, updateData: T) =>
    async (dispatch: MainThunkDispatch, getState: () => State) => {
        const endpoint = apiEndpoints.getSetCollaborator({ id: collaboratorID })
        const body = JSON.stringify({
            appUuid: appID,
            ...updateData
        })
        const { data, error } = await safePromise(
            dispatch(
                apiFetch<APICollaborationModel>(
                    `update_collaborator_${collaboratorID}_${body}`,
                    endpoint,
                    {
                        method: "PATCH",
                        body
                    }
                )
            )
        )

        if (error !== undefined || data === undefined) {
            throw error || Error()
        }

        dispatch({
            type: CollaboratorType.setCollaborator,
            payload: data
        })

        await dispatch(reloadProjectCollaborators(appID))
    }

export const setCollaboratorStatus =
    (appID: string, collaboratorID: number, status: CollaboratorStatus) =>
    async (dispatch: MainThunkDispatch) => {
        const { error } = await safePromise(
            dispatch(
                updateCollaborator<{ collaboratorStatus: CollaboratorStatus }>(
                    collaboratorID,
                    appID,
                    { collaboratorStatus: status }
                )
            )
        )

        if (error !== undefined) {
            const message =
                status === CollaboratorStatus.ACTIVE
                    ? "Could not activate user."
                    : "Could not deactivate user."
            dispatch(failMessage(message, error))
        }
    }

export const setCollaboratorRole =
    (appID: string, collaboratorID: number, role: CollaboratorRole) =>
    async (dispatch: MainThunkDispatch) => {
        const { error } = await safePromise(
            dispatch(
                updateCollaborator<{ collaboratorRole: CollaboratorRole }>(
                    collaboratorID,
                    appID,
                    { collaboratorRole: role }
                )
            )
        )

        if (error !== undefined) {
            dispatch(failMessage("Could not change user role", error))
            return
        }

        dispatch(successMessage("Updated collaborator role successfully"))
    }

export const deleteCollaborator =
    (appID: string, collaborationID: number) =>
    async (dispatch: MainThunkDispatch) => {
        const endpoint = apiEndpoints.deleteCollaborator({ collaborationID })

        try {
            await dispatch(
                apiFetch(endpoint.url, endpoint, { method: "DELETE" })
            )

            dispatch(successMessage("Deleted collaborator successfully"))
            dispatch(reloadProjectCollaborators(appID))
        } catch (error) {
            dispatch(failMessage("Could not delete collaborator", error))
            throw error
        }
    }

export const getUserExistsValue =
    (collaborationId: number, code: string) =>
    async (dispatch: MainThunkDispatch) => {
        try {
            const endpoint = apiEndpoints.collaboratorInvitationUserExists({
                collaborationId,
                code
            })
            const data = await dispatch(
                apiFetch<{ exists: boolean; email: string }>(
                    endpoint.url,
                    endpoint
                )
            )

            if (data === undefined) {
                throw Error("Request throttled")
            }

            dispatch({
                type: APIAuthenticationType.setCollaborationOwnerExists,
                meta: {
                    collaborationId
                },
                payload: data
            })
        } catch (error) {
            dispatch(failMessage("Could not get invitation data", error))
        }
        return
    }

export const acceptInvitation =
    (collaborationID: number, code: string) =>
    async (dispatch: MainThunkDispatch) => {
        try {
            const endpoint = apiEndpoints.acceptInvitation({
                collaborationID,
                code
            })

            await dispatch(apiFetch(endpoint.url, endpoint, { method: "POST" }))
        } catch (error) {
            dispatch(failMessage("Could not accept invitation", error))
            throw error
        }
    }
